import { doc, updateDoc, getDoc, collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { InstructorStatus, UserWithStats, StorageUsageStats } from '../types/admin';
import { createAdminLog } from './adminLog';
import { getStorageLimitBytes } from '../lib/storageQuota';

export async function updateInstructorStatus(
  userId: string,
  status: InstructorStatus,
  adminId: string,
  adminEmail: string
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('ไม่พบผู้ใช้');
    }
    
    const userData = userDoc.data();
    const oldStatus = userData.instructorStatus || 'active';
    
    await updateDoc(userRef, {
      instructorStatus: status,
    });
    
    await createAdminLog(
      'INSTRUCTOR_STATUS',
      userId,
      adminId,
      {
        oldStatus,
        newStatus: status,
      },
      userData.email,
      adminEmail
    );
    
    console.log(`[AdminService] Updated instructor status for ${userId}: ${oldStatus} → ${status}`);
  } catch (error) {
    console.error('[AdminService] Error updating instructor status:', error);
    throw error;
  }
}

export async function updateUserRole(
  userId: string,
  role: 'student' | 'instructor' | 'admin',
  adminId: string,
  adminEmail: string
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('ไม่พบผู้ใช้');
    }
    
    const userData = userDoc.data();
    const oldRole = userData.role;
    
    await updateDoc(userRef, {
      role,
    });
    
    await createAdminLog(
      'ROLE_CHANGE',
      userId,
      adminId,
      {
        oldRole,
        newRole: role,
      },
      userData.email,
      adminEmail
    );
    
    console.log(`[AdminService] Updated role for ${userId}: ${oldRole} → ${role}`);
  } catch (error) {
    console.error('[AdminService] Error updating role:', error);
    throw error;
  }
}

export async function updateUserPlan(
  userId: string,
  plan: string,
  adminId: string,
  adminEmail: string
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('ไม่พบผู้ใช้');
    }
    
    const userData = userDoc.data();
    const oldPlan = userData.plan || 'free';
    const oldSubscriptionTier = userData.subscriptionTier || 'free';
    
    // Update both plan and subscriptionTier to ensure consistency
    await updateDoc(userRef, {
      plan,
      subscriptionTier: plan, // Keep both fields in sync
    });
    
    await createAdminLog(
      'SUBSCRIPTION_UPDATE',
      userId,
      adminId,
      {
        oldPlan,
        newPlan: plan,
        oldSubscriptionTier,
        newSubscriptionTier: plan,
      },
      userData.email,
      adminEmail
    );
    
    console.log(`[AdminService] Updated plan and subscriptionTier for ${userId}: ${oldPlan} → ${plan}`);
  } catch (error) {
    console.error('[AdminService] Error updating plan:', error);
    throw error;
  }
}

export async function getAllUsers(): Promise<UserWithStats[]> {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        uid: doc.id,
        email: data.email || '',
        name: data.name || data.displayName || '',
        role: data.role || 'student',
        plan: data.subscriptionTier || data.plan || 'free',
        subscriptionTier: data.subscriptionTier || data.plan || 'free',
        instructorStatus: data.instructorStatus,
        trialStartedAt: data.trialStartedAt,
        trialExpired: data.trialExpired || false,
        storageUsedBytes: data.storageUsedBytes || 0,
        createdAt: data.createdAt,
        subscriptionEndDate: data.subscriptionEndDate,
      } as UserWithStats;
    });
  } catch (error) {
    console.error('[AdminService] Error fetching users:', error);
    return [];
  }
}

export async function getInstructors(): Promise<UserWithStats[]> {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('role', '==', 'instructor'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        uid: doc.id,
        email: data.email || '',
        name: data.name || data.displayName || '',
        role: 'instructor',
        plan: data.subscriptionTier || data.plan || 'free',
        subscriptionTier: data.subscriptionTier || data.plan || 'free',
        instructorStatus: data.instructorStatus || 'active',
        trialStartedAt: data.trialStartedAt,
        trialExpired: data.trialExpired || false,
        storageUsedBytes: data.storageUsedBytes || 0,
        createdAt: data.createdAt,
        subscriptionEndDate: data.subscriptionEndDate,
      } as UserWithStats;
    });
  } catch (error) {
    console.error('[AdminService] Error fetching instructors:', error);
    return [];
  }
}

export async function getTopStorageUsers(limitCount: number = 5): Promise<StorageUsageStats[]> {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, orderBy('storageUsedBytes', 'desc'), limit(limitCount));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      const usedBytes = data.storageUsedBytes || 0;
      const limitBytes = getStorageLimitBytes({
        plan: data.plan || 'free',
        storageUsedBytes: usedBytes,
        addons: data.addons || { extraStorageGB: 0, active: false },
      });
      
      return {
        uid: doc.id,
        email: data.email || '',
        name: data.name || data.displayName || '',
        usedBytes,
        limitBytes,
        usagePercent: limitBytes > 0 ? (usedBytes / limitBytes) * 100 : 0,
      };
    });
  } catch (error) {
    console.error('[AdminService] Error fetching top storage users:', error);
    return [];
  }
}

export function getInstructorStatusLabel(status: InstructorStatus): string {
  const labels: Record<InstructorStatus, string> = {
    active: 'ใช้งานปกติ',
    limited: 'จำกัดสิทธิ์',
    suspended: 'ระงับการใช้งาน',
  };
  
  return labels[status] || status;
}

export function getInstructorStatusColor(status: InstructorStatus): string {
  const colors: Record<InstructorStatus, string> = {
    active: 'text-green-700 bg-green-100',
    limited: 'text-yellow-700 bg-yellow-100',
    suspended: 'text-red-700 bg-red-100',
  };
  
  return colors[status] || 'text-gray-700 bg-gray-100';
}

export async function updateUserExpiryDate(
  userId: string,
  expiryDate: Date,
  adminId: string,
  adminEmail: string
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('ไม่พบผู้ใช้');
    }
    
    const userData = userDoc.data();
    const oldExpiryDate = userData.subscriptionEndDate;
    
    // Update user's subscriptionEndDate
    await updateDoc(userRef, {
      subscriptionEndDate: expiryDate,
    });
    
    // Update subscription's endDate in subscriptions collection
    const subscriptionsQuery = query(
      collection(db, 'subscriptions'),
      where('userId', '==', userId),
      where('status', '==', 'active')
    );
    const subscriptionsSnapshot = await getDocs(subscriptionsQuery);
    
    if (!subscriptionsSnapshot.empty) {
      const subscriptionDoc = subscriptionsSnapshot.docs[0];
      await updateDoc(doc(db, 'subscriptions', subscriptionDoc.id), {
        endDate: expiryDate,
        updatedAt: Timestamp.now(),
      });
      console.log(`[AdminService] Updated subscription endDate for ${userId}`);
    }
    
    await createAdminLog(
      'SUBSCRIPTION_UPDATE',
      userId,
      adminId,
      {
        oldExpiryDate: oldExpiryDate ? oldExpiryDate.toDate().toISOString() : null,
        newExpiryDate: expiryDate.toISOString(),
      },
      userData.email,
      adminEmail
    );
    
    console.log(`[AdminService] Updated expiry date for ${userId}: ${oldExpiryDate?.toDate()} → ${expiryDate}`);
  } catch (error) {
    console.error('[AdminService] Error updating expiry date:', error);
    throw error;
  }
}

export async function deleteUser(
  userId: string,
  adminId: string,
  adminEmail: string
): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('ไม่พบผู้ใช้');
    }
    
    const userData = userDoc.data();
    const userEmail = userData.email || 'ไม่ระบุ';
    
    // Log before deletion
    await createAdminLog(
      'USER_DELETE',
      userId,
      adminId,
      {
        email: userEmail,
        role: userData.role,
        plan: userData.plan || userData.subscriptionTier,
      },
      userEmail,
      adminEmail
    );
    
    // Delete related data from various collections
    const collectionsToClean = [
      'subscriptions',
      'subscription_payments',
      'subscriptionHistory',
      'omise_subscriptions',
      'urlAddons',
      'salePagePaymentNotifications',
      'enrollments',
      'progress',
      'certificates',
      'notifications',
      'studentProfiles',
      'watchHistory',
      'analytics'
    ];
    
    for (const collectionName of collectionsToClean) {
      const q = query(collection(db, collectionName), where('userId', '==', userId));
      const snapshot = await getDocs(q);
      
      for (const docSnapshot of snapshot.docs) {
        await docSnapshot.ref.delete();
      }
      
      if (!snapshot.empty) {
        console.log(`[AdminService] Deleted ${snapshot.size} documents from ${collectionName}`);
      }
    }
    
    // Delete courses created by this user
    const coursesQuery = query(collection(db, 'courses'), where('createdBy', '==', userId));
    const coursesSnapshot = await getDocs(coursesQuery);
    
    for (const courseDoc of coursesSnapshot.docs) {
      await courseDoc.ref.delete();
    }
    
    if (!coursesSnapshot.empty) {
      console.log(`[AdminService] Deleted ${coursesSnapshot.size} courses created by user`);
    }
    
    // Delete sale pages created by this user
    const salePagesQuery = query(collection(db, 'salePages'), where('userId', '==', userId));
    const salePagesSnapshot = await getDocs(salePagesQuery);
    
    for (const salePageDoc of salePagesSnapshot.docs) {
      await salePageDoc.ref.delete();
    }
    
    if (!salePagesSnapshot.empty) {
      console.log(`[AdminService] Deleted ${salePagesSnapshot.size} sale pages created by user`);
    }
    
    // Finally, delete the user document
    await userRef.delete();
    
    console.log(`[AdminService] Successfully deleted user ${userId} (${userEmail})`);
  } catch (error) {
    console.error('[AdminService] Error deleting user:', error);
    throw error;
  }
}
